import { ChevronLeft, ChevronRight, X } from 'lucide-react-native';
import { useEffect, useRef, useState } from 'react';
import {
  Dimensions,
  Image,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from 'react-native';
import {
  Gesture,
  GestureDetector,
  GestureHandlerRootView,
} from 'react-native-gesture-handler';
import Animated, {
  Easing,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated';

import { Fonts, Palette } from '@/constants/theme';
import { webNoOutline } from '@/lib/web-style';

/**
 * Full-screen photo viewer with horizontal swipe through a URI list.
 * Cross-platform: paging ScrollView on every platform — no extra dep.
 * Tapping the backdrop or the X dismisses; left/right chevrons step on
 * platforms where swipe isn't obvious (web with mouse, tablets in
 * landscape).
 */
export function PhotoLightbox({
  uris,
  startIndex = 0,
  onClose,
  captions,
  onCaptionChange,
}: {
  uris: string[] | null;
  startIndex?: number;
  onClose: () => void;
  /** Optional caption map keyed by URI. Displayed at the bottom. */
  captions?: Record<string, string>;
  /** If supplied, the caption becomes editable inline (TextInput). */
  onCaptionChange?: (uri: string, value: string) => void;
}) {
  const open = uris != null && uris.length > 0;
  const [index, setIndex] = useState(startIndex);
  const scrollRef = useRef<ScrollView | null>(null);
  const { width, height } = Dimensions.get('window');
  const anim = useSharedValue(0);

  useEffect(() => {
    anim.value = withTiming(open ? 1 : 0, {
      duration: 200,
      easing: Easing.out(Easing.cubic),
    });
    if (open) setIndex(startIndex);
  }, [open, startIndex, anim]);

  useEffect(() => {
    if (!open) return;
    // After the modal mounts and ScrollView lays out, jump to the start
    // index — avoids a flash of the first image when opening at index N.
    const t = setTimeout(() => {
      scrollRef.current?.scrollTo({ x: startIndex * width, animated: false });
    }, 30);
    return () => clearTimeout(t);
  }, [open, startIndex, width]);

  const backdropStyle = useAnimatedStyle(() => ({ opacity: anim.value }));
  const stageStyle = useAnimatedStyle(() => ({ opacity: anim.value }));

  if (!uris) return null;

  const onScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const x = e.nativeEvent.contentOffset.x;
    const i = Math.round(x / width);
    if (i !== index && i >= 0 && i < uris.length) setIndex(i);
  };

  const step = (delta: number) => {
    const next = Math.max(0, Math.min(uris.length - 1, index + delta));
    scrollRef.current?.scrollTo({ x: next * width, animated: true });
    setIndex(next);
  };

  return (
    <Modal animationType="none" transparent visible={open} onRequestClose={onClose}>
      <Animated.View style={[styles.backdrop, backdropStyle]} />
      <GestureHandlerRootView style={styles.stage}>
        <Animated.View style={[styles.stage, stageStyle]}>
          <ScrollView
            ref={scrollRef}
            horizontal
            pagingEnabled
            showsHorizontalScrollIndicator={false}
            onScroll={onScroll}
            scrollEventThrottle={16}
            style={{ width, height }}>
            {uris.map((uri, i) => (
              <ZoomableImage
                key={`${uri}-${i}`}
                uri={uri}
                width={width}
                height={height}
                onCloseTap={onClose}
                active={i === index}
              />
            ))}
          </ScrollView>

        {/* Topbar */}
        <View style={styles.topbar} pointerEvents="box-none">
          <View style={styles.counter}>
            <Text style={styles.counterText}>
              {index + 1} / {uris.length}
            </Text>
          </View>
          <Pressable
            onPress={onClose}
            hitSlop={12}
            style={({ pressed }) => [styles.closeBtn, pressed && { opacity: 0.75 }]}
            accessibilityLabel="Close">
            <X size={20} color={Palette.cream} strokeWidth={2} />
          </Pressable>
        </View>

        {/* Caption strip — read-only or editable depending on props. */}
        {uris[index] != null && (captions || onCaptionChange) ? (
          <View style={styles.captionBar} pointerEvents="box-none">
            {onCaptionChange ? (
              <TextInput
                value={captions?.[uris[index]] ?? ''}
                onChangeText={(v) => onCaptionChange(uris[index], v)}
                placeholder="Add a caption (serial #, condition, scratch on the left side…)"
                placeholderTextColor="rgba(251,246,234,0.45)"
                style={[styles.captionInput, webNoOutline]}
                multiline
              />
            ) : captions?.[uris[index]] ? (
              <Text style={styles.captionText}>{captions[uris[index]]}</Text>
            ) : null}
          </View>
        ) : null}

        {/* Step controls — useful with mouse / when swipe isn't obvious. */}
        {uris.length > 1 ? (
          <>
            {index > 0 ? (
              <Pressable
                onPress={() => step(-1)}
                hitSlop={12}
                style={[styles.nav, styles.navLeft]}
                accessibilityLabel="Previous photo">
                <ChevronLeft size={22} color={Palette.cream} strokeWidth={2} />
              </Pressable>
            ) : null}
            {index < uris.length - 1 ? (
              <Pressable
                onPress={() => step(1)}
                hitSlop={12}
                style={[styles.nav, styles.navRight]}
                accessibilityLabel="Next photo">
                <ChevronRight size={22} color={Palette.cream} strokeWidth={2} />
              </Pressable>
            ) : null}
          </>
        ) : null}
        </Animated.View>
      </GestureHandlerRootView>
    </Modal>
  );
}

/**
 * Pinch + double-tap zoomable image. Resets to fit when the carousel
 * scrolls to a different page (via the `active` prop). Single tap closes
 * the lightbox via the supplied callback.
 */
function ZoomableImage({
  uri,
  width,
  height,
  onCloseTap,
  active,
}: {
  uri: string;
  width: number;
  height: number;
  onCloseTap: () => void;
  active: boolean;
}) {
  const scale = useSharedValue(1);
  const savedScale = useSharedValue(1);
  const translateX = useSharedValue(0);
  const translateY = useSharedValue(0);
  const savedTranslateX = useSharedValue(0);
  const savedTranslateY = useSharedValue(0);

  // Reset to fit when this page is no longer the active one — otherwise
  // a zoomed-in image leaks visually when the user swipes away.
  useEffect(() => {
    if (!active) {
      scale.value = 1;
      savedScale.value = 1;
      translateX.value = 0;
      translateY.value = 0;
      savedTranslateX.value = 0;
      savedTranslateY.value = 0;
    }
  }, [active, scale, savedScale, translateX, translateY, savedTranslateX, savedTranslateY]);

  const pinch = Gesture.Pinch()
    .onUpdate((e) => {
      scale.value = Math.max(1, Math.min(6, savedScale.value * e.scale));
    })
    .onEnd(() => {
      savedScale.value = scale.value;
      if (scale.value <= 1.05) {
        scale.value = withSpring(1);
        savedScale.value = 1;
        translateX.value = withSpring(0);
        translateY.value = withSpring(0);
        savedTranslateX.value = 0;
        savedTranslateY.value = 0;
      }
    });

  const pan = Gesture.Pan()
    .minPointers(1)
    .maxPointers(1)
    .onUpdate((e) => {
      if (scale.value <= 1) return;
      translateX.value = savedTranslateX.value + e.translationX;
      translateY.value = savedTranslateY.value + e.translationY;
    })
    .onEnd(() => {
      savedTranslateX.value = translateX.value;
      savedTranslateY.value = translateY.value;
    });

  const doubleTap = Gesture.Tap()
    .numberOfTaps(2)
    .onEnd(() => {
      if (scale.value > 1) {
        scale.value = withSpring(1);
        savedScale.value = 1;
        translateX.value = withSpring(0);
        translateY.value = withSpring(0);
        savedTranslateX.value = 0;
        savedTranslateY.value = 0;
      } else {
        scale.value = withSpring(2.5);
        savedScale.value = 2.5;
      }
    });

  const singleTap = Gesture.Tap()
    .numberOfTaps(1)
    .onEnd(() => {
      if (scale.value > 1.05) return;
      runOnJS(onCloseTap)();
    });

  const composed = Gesture.Simultaneous(
    pinch,
    pan,
    Gesture.Exclusive(doubleTap, singleTap)
  );

  const imgStyle = useAnimatedStyle(() => ({
    transform: [
      { translateX: translateX.value },
      { translateY: translateY.value },
      { scale: scale.value },
    ],
  }));

  return (
    <GestureDetector gesture={composed}>
      <Animated.View style={[styles.page, { width, height }]}>
        <Animated.Image
          source={{ uri }}
          style={[styles.img, imgStyle]}
          resizeMode="contain"
        />
      </Animated.View>
    </GestureDetector>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.95)',
  },
  stage: { ...StyleSheet.absoluteFillObject },
  page: { alignItems: 'center', justifyContent: 'center' },
  img: { width: '100%', height: '100%' },
  topbar: {
    position: 'absolute', top: 0, left: 0, right: 0,
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingTop: 44, paddingHorizontal: 16,
  },
  counter: {
    paddingHorizontal: 10, paddingVertical: 4, borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.18)',
  },
  counterText: {
    fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.cream, letterSpacing: 0.3,
  },
  closeBtn: {
    width: 38, height: 38, borderRadius: 19,
    backgroundColor: 'rgba(255,255,255,0.18)',
    alignItems: 'center', justifyContent: 'center',
  },
  nav: {
    position: 'absolute', top: '50%', marginTop: -22,
    width: 44, height: 44, borderRadius: 22,
    backgroundColor: 'rgba(255,255,255,0.18)',
    alignItems: 'center', justifyContent: 'center',
  },
  navLeft: { left: 12 },
  navRight: { right: 12 },
  captionBar: {
    position: 'absolute',
    bottom: 32, left: 16, right: 16,
  },
  captionInput: {
    fontFamily: Fonts.sansSemibold, fontSize: 13.5, color: Palette.cream,
    backgroundColor: 'rgba(255,255,255,0.12)',
    borderRadius: 12, paddingHorizontal: 12, paddingVertical: 10,
    minHeight: 44,
  },
  captionText: {
    fontFamily: Fonts.sansSemibold, fontSize: 13.5, color: Palette.cream,
    backgroundColor: 'rgba(255,255,255,0.12)',
    borderRadius: 12, paddingHorizontal: 12, paddingVertical: 10,
    lineHeight: 19,
  },
});
